import { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { RaceFormat, RaceVisibility } from '@/gql/graphql';
import { Calendar, Globe, Lock, Trophy, Loader2, AlignLeft, Users, Mountain, Ruler, LockKeyhole, RotateCcw } from 'lucide-react';
import { cn, toLocalISOString } from '@/lib/utils';
import { FORMAT_BADGE_STYLES } from '@/lib/constants';

export interface RaceFormData {
    name: string;
    description?: string;
    raceFormat: RaceFormat;
    distance?: number | null;
    elevationGain?: number | null;
    bettingDeadline: string;
    visibility: RaceVisibility;
    totalRunners?: number | null;
    tokenCount?: number | null;
}

interface RaceFormProps {
    initialValues?: Partial<RaceFormData>;
    onSubmit: (data: RaceFormData) => Promise<void>;
    isLoading: boolean;
    submitLabel: string;
    lockedFields?: Array<keyof RaceFormData>;
    lockedFieldMessages?: Partial<Record<keyof RaceFormData, string>>;
    onDirtyChange?: (isDirty: boolean) => void;
    /** If set, form values are auto-saved to localStorage under this key */
    draftKey?: string;
    /** Whether tokenCount is locked (bets already placed) */
    tokenCountLocked?: boolean;
}

const RACE_FORMATS = [
    { value: RaceFormat['20K'], ...FORMAT_BADGE_STYLES._20K },
    { value: RaceFormat['50K'], ...FORMAT_BADGE_STYLES._50K },
    { value: RaceFormat['100K'], ...FORMAT_BADGE_STYLES._100K },
    { value: RaceFormat['100M'], ...FORMAT_BADGE_STYLES._100M },
];

function loadDraft(key: string): Partial<RaceFormData> | null {
    try {
        const stored = localStorage.getItem(`draft:${key}`);
        return stored ? JSON.parse(stored) : null;
    } catch { return null; }
}

export function RaceForm({ initialValues, onSubmit, isLoading, submitLabel, lockedFields = [], lockedFieldMessages = {}, onDirtyChange, draftKey, tokenCountLocked }: RaceFormProps) {
    const { t } = useTranslation('races');
    const isLocked = (field: keyof RaceFormData) => lockedFields.includes(field);

    // Load draft if available (only on mount)
    const [draftLoaded] = useState(() => draftKey ? loadDraft(draftKey) : null);
    const [showDraftBanner, setShowDraftBanner] = useState(!!draftLoaded);
    const init = draftLoaded ?? initialValues;

    const [name, setName] = useState(init?.name || '');
    const [description, setDescription] = useState(init?.description || '');

    const [raceFormat, setRaceFormat] = useState<RaceFormat>(
        init?.raceFormat || RaceFormat['100K']
    );
    const [distance, setDistance] = useState<string>(
        init?.distance?.toString() || ''
    );
    const [elevationGain, setElevationGain] = useState<string>(
        init?.elevationGain?.toString() || ''
    );

    const getDefaultDate = () => {
        const d = new Date();
        d.setDate(d.getDate() + 7);
        return d;
    };

    const [deadline, setDeadline] = useState(
        init?.bettingDeadline
            ? toLocalISOString(init.bettingDeadline)
            : toLocalISOString(getDefaultDate())
    );

    const [totalRunners, setTotalRunners] = useState<string>(
        init?.totalRunners?.toString() || ''
    );

    const [tokenCount, setTokenCount] = useState<string>(
        init?.tokenCount?.toString() || ''
    );

    const [visibility, setVisibility] = useState<RaceVisibility>(
        init?.visibility || RaceVisibility.Public
    );

    const handleSubmit = (e?: React.FormEvent) => {
        e?.preventDefault();
        clearDraft();
        const isoDeadline = new Date(deadline).toISOString();
        const parsedTotalRunners = totalRunners ? parseInt(totalRunners, 10) : null;
        const parsedDistance = distance ? parseFloat(distance) : null;
        const parsedElevation = elevationGain ? parseInt(elevationGain, 10) : null;
        const parsedTokenCount = tokenCount ? parseInt(tokenCount, 10) : null;
        onSubmit({
            name,
            description,
            raceFormat,
            distance: parsedDistance && parsedDistance > 0 ? parsedDistance : null,
            elevationGain: parsedElevation && parsedElevation > 0 ? parsedElevation : null,
            bettingDeadline: isoDeadline,
            visibility,
            totalRunners: parsedTotalRunners && parsedTotalRunners > 0 ? parsedTotalRunners : null,
            tokenCount: parsedTokenCount && parsedTokenCount > 0 ? parsedTokenCount : null,
        });
    };

    const isFormValid = name.trim().length > 2 && deadline;

    const isDirty = useMemo(() => {
        if (!initialValues) return name.trim().length > 0;
        return (
            name !== (initialValues.name || '') ||
            description !== (initialValues.description || '') ||
            raceFormat !== (initialValues.raceFormat || RaceFormat['100K']) ||
            distance !== (initialValues.distance?.toString() || '') ||
            elevationGain !== (initialValues.elevationGain?.toString() || '') ||
            deadline !== (initialValues.bettingDeadline ? toLocalISOString(initialValues.bettingDeadline) : toLocalISOString(getDefaultDate())) ||
            totalRunners !== (initialValues.totalRunners?.toString() || '') ||
            tokenCount !== (initialValues.tokenCount?.toString() || '') ||
            visibility !== (initialValues.visibility || RaceVisibility.Public)
        );
    }, [name, description, raceFormat, distance, elevationGain, deadline, totalRunners, tokenCount, visibility, initialValues]);

    useEffect(() => {
        onDirtyChange?.(isDirty);
    }, [isDirty, onDirtyChange]);

    // Auto-save draft to localStorage (debounced)
    const draftTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    useEffect(() => {
        if (!draftKey) return;
        if (draftTimerRef.current) clearTimeout(draftTimerRef.current);
        draftTimerRef.current = setTimeout(() => {
            try {
                const draft: Partial<RaceFormData> = {
                    name, description, raceFormat, bettingDeadline: deadline, visibility,
                    distance: distance ? parseFloat(distance) : null,
                    elevationGain: elevationGain ? parseInt(elevationGain, 10) : null,
                    totalRunners: totalRunners ? parseInt(totalRunners, 10) : null,
                    tokenCount: tokenCount ? parseInt(tokenCount, 10) : null,
                };
                localStorage.setItem(`draft:${draftKey}`, JSON.stringify(draft));
            } catch { /* ignore quota errors */ }
        }, 500);
        return () => { if (draftTimerRef.current) clearTimeout(draftTimerRef.current); };
    }, [draftKey, name, description, raceFormat, distance, elevationGain, deadline, totalRunners, tokenCount, visibility]);

    const clearDraft = useCallback(() => {
        if (draftKey) {
            try { localStorage.removeItem(`draft:${draftKey}`); } catch { /* ignore */ }
        }
    }, [draftKey]);

    const discardDraft = useCallback(() => {
        setName(initialValues?.name || '');
        setDescription(initialValues?.description || '');
        setRaceFormat(initialValues?.raceFormat || RaceFormat['100K']);
        setDistance(initialValues?.distance?.toString() || '');
        setElevationGain(initialValues?.elevationGain?.toString() || '');
        setDeadline(initialValues?.bettingDeadline ? toLocalISOString(initialValues.bettingDeadline) : toLocalISOString(getDefaultDate()));
        setTotalRunners(initialValues?.totalRunners?.toString() || '');
        setTokenCount(initialValues?.tokenCount?.toString() || '');
        setVisibility(initialValues?.visibility || RaceVisibility.Public);
        clearDraft();
        setShowDraftBanner(false);
    }, [initialValues, clearDraft]);

    return (
        <form onSubmit={handleSubmit} className="space-y-6 w-full">

            {/* Draft restored banner */}
            {showDraftBanner && (
                <div className="rounded-2xl bg-blue-50 p-4 border border-blue-100 flex items-center justify-between gap-3">
                    <p className="text-sm font-medium text-blue-800">{t('form.draftRestored')}</p>
                    <button
                        type="button"
                        onClick={discardDraft}
                        className="flex items-center gap-1 text-xs font-bold text-blue-600 hover:text-blue-800 transition-colors shrink-0"
                    >
                        <RotateCcw size={12} />
                        {t('form.draftDiscard')}
                    </button>
                </div>
            )}

            {/* ====== SECTION: INFORMATIONS ====== */}
            <h3 className="text-xs font-bold text-slate-400 uppercase tracking-widest">{t('form.section.info')}</h3>

            {/* --- NOM --- */}
            <div className="space-y-2">
                <label className="flex items-center gap-2 text-sm font-bold text-slate-700 ml-1">
                    <Trophy size={16} className="text-emerald-500" />
                    {t('form.name.label')} <span className="text-red-400">*</span>
                </label>
                <input
                    className="w-full h-14 px-4 bg-slate-50 border border-slate-100 rounded-2xl focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 outline-none transition-all font-medium placeholder:text-slate-400 text-slate-900"
                    placeholder={t('form.name.placeholder')}
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                />
            </div>

            {/* --- DESCRIPTION --- */}
            <div className="space-y-2">
                <label className="flex items-center gap-2 text-sm font-bold text-slate-700 ml-1">
                    <AlignLeft size={16} className="text-emerald-500" />
                    {t('form.description.label')}
                </label>
                <textarea
                    className="w-full p-4 bg-slate-50 border border-slate-100 rounded-2xl focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 outline-none transition-all font-medium placeholder:text-slate-400 text-slate-900 min-h-[100px] resize-y"
                    placeholder={t('form.description.placeholder')}
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                />
            </div>

            {/* ====== SECTION: COURSE ====== */}
            <div className="border-t border-slate-100 pt-5">
                <h3 className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-6">{t('form.section.race')}</h3>
            </div>

            {/* --- FORMAT DE COURSE --- */}
            <div className={cn("space-y-3", isLocked('raceFormat') && "opacity-60")}>
                <label className="flex items-center gap-2 text-sm font-bold text-slate-700 ml-1">
                    <Mountain size={16} className="text-emerald-500" />
                    {t('form.raceFormat.label')} <span className="text-red-400">*</span>
                    {isLocked('raceFormat') && <LockKeyhole size={14} className="text-slate-400" />}
                </label>
                <div className={cn("grid grid-cols-4 gap-2", isLocked('raceFormat') && "pointer-events-none")}>
                    {RACE_FORMATS.map(({ value, label, color, bg, text }) => {
                        const isSelected = raceFormat === value;
                        return (
                            <button
                                key={value}
                                type="button"
                                onClick={() => setRaceFormat(value)}
                                className={cn(
                                    "flex items-center justify-center p-3 rounded-xl border-2 transition-all font-bold text-sm h-12",
                                    !isSelected && "border-slate-100 bg-white text-slate-400 hover:border-slate-200"
                                )}
                                style={isSelected ? {
                                    borderColor: color,
                                    backgroundColor: bg,
                                    color: text,
                                    boxShadow: `0 0 0 1px ${color}33`,
                                } : undefined}
                            >
                                {label}
                            </button>
                        );
                    })}
                </div>
            </div>

            {/* --- DISTANCE & DÉNIVELÉ --- */}
            <div className="grid grid-cols-2 gap-4">
                <div className={cn("space-y-2", isLocked('distance') && "opacity-60")}>
                    <label className="flex items-center gap-2 text-sm font-bold text-slate-700 ml-1">
                        <Ruler size={16} className="text-emerald-500" />
                        {t('form.distance.label')}
                        {isLocked('distance') && <LockKeyhole size={14} className="text-slate-400" />}
                    </label>
                    <input
                        type="number"
                        min="0"
                        step="0.1"
                        className="w-full h-14 px-4 bg-slate-50 border border-slate-100 rounded-2xl focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 outline-none transition-all font-medium placeholder:text-slate-400 text-slate-900"
                        placeholder={t('form.distance.placeholder')}
                        value={distance}
                        onChange={(e) => setDistance(e.target.value)}
                        readOnly={isLocked('distance')}
                    />
                </div>
                <div className={cn("space-y-2", isLocked('elevationGain') && "opacity-60")}>
                    <label className="flex items-center gap-2 text-sm font-bold text-slate-700 ml-1">
                        <Mountain size={16} className="text-emerald-500" />
                        {t('form.elevationGain.label')}
                        {isLocked('elevationGain') && <LockKeyhole size={14} className="text-slate-400" />}
                    </label>
                    <input
                        type="number"
                        min="0"
                        step="1"
                        className="w-full h-14 px-4 bg-slate-50 border border-slate-100 rounded-2xl focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 outline-none transition-all font-medium placeholder:text-slate-400 text-slate-900"
                        placeholder={t('form.elevationGain.placeholder')}
                        value={elevationGain}
                        onChange={(e) => setElevationGain(e.target.value)}
                        readOnly={isLocked('elevationGain')}
                    />
                </div>
            </div>

            {/* --- TOTAL RUNNERS --- */}
            <div className="space-y-2">
                <label className="flex items-center gap-2 text-sm font-bold text-slate-700 ml-1">
                    <Users size={16} className="text-emerald-500" />
                    {t('form.totalRunners.label')}
                </label>
                <input
                    type="number"
                    min="1"
                    className="w-full h-14 px-4 bg-slate-50 border border-slate-100 rounded-2xl focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 outline-none transition-all font-medium placeholder:text-slate-400 text-slate-900"
                    placeholder={t('form.totalRunners.placeholder')}
                    value={totalRunners}
                    onChange={(e) => setTotalRunners(e.target.value)}
                />
                <p className="text-xs text-slate-400 px-1">
                    {t('form.totalRunners.help')}
                </p>
            </div>

            {/* --- TOKEN COUNT --- */}
            <div className={cn("space-y-2", tokenCountLocked && "opacity-60")}>
                <label className="flex items-center gap-2 text-sm font-bold text-slate-700 ml-1">
                    <Trophy size={16} className="text-amber-500" />
                    {t('form.tokenCount.label')}
                </label>
                <input
                    type="number"
                    min="1"
                    max={totalRunners ? parseInt(totalRunners, 10) : undefined}
                    className="w-full h-14 px-4 bg-slate-50 border border-slate-100 rounded-2xl focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 outline-none transition-all font-medium placeholder:text-slate-400 text-slate-900"
                    placeholder={t('form.tokenCount.placeholder')}
                    value={tokenCount}
                    onChange={(e) => setTokenCount(e.target.value)}
                    readOnly={tokenCountLocked}
                />
                <div className="flex items-center gap-2 px-1">
                    <span className="text-xs text-slate-400">{t('form.tokenCount.presets')}</span>
                    {[6, 10, 20].map((preset) => (
                        <button
                            key={preset}
                            type="button"
                            disabled={tokenCountLocked}
                            onClick={() => setTokenCount(String(preset))}
                            className={cn(
                                "px-3 py-1 rounded-full text-xs font-bold transition-all",
                                tokenCount === String(preset)
                                    ? "bg-emerald-100 text-emerald-700 border border-emerald-200"
                                    : "bg-slate-100 text-slate-500 hover:bg-slate-200 border border-transparent"
                            )}
                        >
                            {preset}
                        </button>
                    ))}
                </div>
                <p className="text-xs text-slate-400 px-1">
                    {tokenCountLocked ? t('form.tokenCount.locked') : t('form.tokenCount.help')}
                </p>
            </div>

            {/* ====== SECTION: PARAMETRES ====== */}
            <div className="border-t border-slate-100 pt-5">
                <h3 className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-6">{t('form.section.settings')}</h3>
            </div>

            {/* --- DEADLINE --- */}
            <div className="space-y-2">
                <label className="flex items-center gap-2 text-sm font-bold text-slate-700 ml-1">
                    <Calendar size={16} className="text-emerald-500" />
                    {t('form.deadline.label')} <span className="text-red-400">*</span>
                </label>
                <div className="relative overflow-hidden rounded-2xl">
                    <style>{`
                      input[type="datetime-local"]::-webkit-calendar-picker-indicator {
                        display: none;
                        -webkit-appearance: none;
                      }
                      input[type="datetime-local"]::-webkit-date-and-time-value {
                        text-align: left;
                      }
                    `}</style>

                    <input
                        type="datetime-local"
                        className="w-full max-w-full h-14 px-4 pr-12 bg-slate-50 border border-slate-100 rounded-2xl focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 outline-none transition-all font-medium text-slate-900 appearance-none cursor-pointer text-base"
                        value={deadline}
                        min={!initialValues ? toLocalISOString(new Date().toISOString().slice(0, 16)) : undefined}
                        onChange={(e) => setDeadline(e.target.value)}
                        onClick={(e) => e.currentTarget.showPicker?.()}
                    />

                    <div className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none text-slate-400">
                        <Calendar size={20} />
                    </div>
                </div>
                <p className="text-xs text-slate-400 px-1">
                    {t('form.deadline.help')}
                </p>
            </div>

            {/* --- VISIBILITÉ --- */}
            <div className={cn("space-y-3 pt-2", isLocked('visibility') && "opacity-60")}>
                <label className="flex items-center gap-2 text-sm font-bold text-slate-700 ml-1">
                    {t('form.visibility.label')}
                    {isLocked('visibility') && <LockKeyhole size={14} className="text-slate-400" />}
                </label>
                <div className={cn("flex gap-3", isLocked('visibility') && "pointer-events-none")}>
                    <button
                        type="button"
                        onClick={() => setVisibility(RaceVisibility.Public)}
                        className={cn(
                            "flex-1 flex flex-col items-center justify-center p-3 rounded-xl border transition-all gap-1 h-24",
                            visibility === RaceVisibility.Public
                                ? "border-emerald-500 bg-emerald-50/50 text-emerald-900 ring-1 ring-emerald-500/20"
                                : "border-slate-100 bg-white text-slate-500 hover:border-slate-200"
                        )}
                    >
                        <div className={cn("p-1.5 rounded-full mb-1", visibility === RaceVisibility.Public ? "bg-emerald-100 text-emerald-600" : "bg-slate-100 text-slate-400")}>
                            <Globe size={20} />
                        </div>
                        <span className="text-sm font-bold">{t('form.visibility.public')}</span>
                        <span className="text-[10px] opacity-70">{t('form.visibility.publicDesc')}</span>
                    </button>

                    <button
                        type="button"
                        onClick={() => setVisibility(RaceVisibility.Private)}
                        className={cn(
                            "flex-1 flex flex-col items-center justify-center p-3 rounded-xl border transition-all gap-1 h-24",
                            visibility === RaceVisibility.Private
                                ? "border-emerald-500 bg-emerald-50/50 text-emerald-900 ring-1 ring-emerald-500/20"
                                : "border-slate-100 bg-white text-slate-500 hover:border-slate-200"
                        )}
                    >
                        <div className={cn("p-1.5 rounded-full mb-1", visibility === RaceVisibility.Private ? "bg-emerald-100 text-emerald-600" : "bg-slate-100 text-slate-400")}>
                            <Lock size={20} />
                        </div>
                        <span className="text-sm font-bold">{t('form.visibility.private')}</span>
                        <span className="text-[10px] opacity-70">{t('form.visibility.privateDesc')}</span>
                    </button>
                </div>
                {lockedFieldMessages.visibility && (
                    <p className="text-xs text-amber-600 px-1 font-medium">{lockedFieldMessages.visibility}</p>
                )}
            </div>

            {/* --- SUBMIT --- */}
            <div className="pt-4">
                <button
                    type="submit"
                    disabled={isLoading || !isFormValid || (!!initialValues && !isDirty)}
                    className={cn(
                        "w-full h-14 rounded-2xl font-bold text-lg flex items-center justify-center gap-2 transition-all active:scale-95 shadow-lg shadow-emerald-100/50",
                        isLoading || !isFormValid || (!!initialValues && !isDirty)
                            ? "bg-slate-100 text-slate-400 cursor-not-allowed shadow-none"
                            : "bg-emerald-600 text-white hover:bg-emerald-700"
                    )}
                >
                    {isLoading ? <Loader2 size={24} className="animate-spin" /> : submitLabel}
                </button>
            </div>
        </form>
    );
}
